import * as THREE from 'three'
import { useRef, useState, useMemo, useEffect, Suspense } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Billboard, Text, Html } from '@react-three/drei'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'

// --- Updated word list with routes and descriptions ---
const advertisingWords = [
    // Platforms
    { text: 'Google', color: '#4285F4', path: '/services/paid-media/ppc', description: 'Google Ads (PPC) – drive targeted traffic and maximize ROI.' },
    { text: 'Meta', color: '#0084FF', path: '/services/paid-media/paid-social', description: 'Meta advertising across Facebook and Instagram.' },
    { text: 'Facebook', color: '#1877F2', path: '/services/paid-media/paid-social', description: 'Engage audiences on Facebook with precise targeting.' },
    { text: 'Instagram', color: '#E4405F', path: '/services/paid-media/paid-social', description: 'Visual storytelling and ads on Instagram.' },
    { text: 'TikTok', color: '#000000', path: '/services/paid-media/paid-social', description: 'Short‑form video ads that capture attention.' },
    { text: 'YouTube', color: '#FF0000', path: '/services/experience/video', description: 'Video ads and content on YouTube.' },
    { text: 'X', color: '#000000', path: '/services/paid-media/paid-social', description: 'Promote your brand on X (formerly Twitter).' },
    { text: 'Pinterest', color: '#E60023', path: '/services/paid-media/paid-social', description: 'Inspire action with Pinterest ads.' },
    { text: 'LinkedIn', color: '#0A66C2', path: '/services/paid-media/paid-social', description: 'B2B advertising on LinkedIn.' },

    // Generic terms
    { text: 'Ads', color: '#FF6B35', path: '/services/paid-media', description: 'Multi‑channel ad campaigns tailored to your goals.' },
    { text: 'PPC', color: '#4ECDC4', path: '/services/paid-media/ppc', description: 'Pay‑per‑click campaigns that deliver measurable results.' },
    { text: 'CPM', color: '#45B7D1', path: '/services/paid-media/programmatic', description: 'Cost‑per‑mille programmatic advertising.' },
    { text: 'SEO', color: '#96CEB4', path: '/services/seo', description: 'Search engine optimisation for organic growth.' },
    { text: 'SEM', color: '#FEA47F', path: '/services/paid-media/ppc', description: 'Search engine marketing – paid & organic.' },
    { text: 'VideoAds', color: '#FF9FF3', path: '/services/paid-media/paid-social', description: 'Engaging video creatives for social platforms.' },
    { text: 'Video', color: '#F368E0', path: '/services/experience/video', description: 'Professional video production and strategy.' },
    { text: 'Social', color: '#FF6B6B', path: '/services/paid-media/paid-social', description: 'Social media advertising and management.' },
    { text: 'Native', color: '#48DBFB', path: '/services/paid-media/programmatic', description: 'Native ads that blend with content.' },
    { text: 'Retarget', color: '#1DD1A1', path: '/services/paid-media/ppc', description: 'Re‑engage users who have shown interest.' },
    { text: 'Remarket', color: '#10AC84', path: '/services/paid-media/ppc', description: 'Powerful remarketing strategies.' },
    { text: 'Search', color: '#54A0FF', path: '/services/seo', description: 'Search‑driven traffic and visibility.' },
    { text: 'Leads', color: '#5F27CD', path: '/services/paid-media', description: 'Lead generation campaigns that convert.' },
    { text: 'Performance', color: '#FF9F43', path: '/services/paid-media', description: 'Performance marketing focused on ROI.' },
    { text: 'Banner', color: '#00D2D3', path: '/services/paid-media/programmatic', description: 'Display and banner advertising.' },
    { text: 'Carousel', color: '#FF9FF3', path: '/services/paid-media/paid-social', description: 'Interactive carousel ads for social feeds.' },
    { text: 'Campaign', color: '#FECA57', path: '/services/paid-media', description: 'End‑to‑end campaign management.' },
    { text: 'Marketing', color: '#FF6B6B', path: '/services/strategy', description: 'Holistic marketing strategies.' },
    { text: 'Promo', color: '#48DBFB', path: '/services/paid-media', description: 'Promotional campaigns to boost sales.' },
    { text: 'Strategy', color: '#1DD1A1', path: '/services/strategy', description: 'Data‑driven marketing strategy.' },
    { text: 'Audience', color: '#5F27CD', path: '/services/data-ai', description: 'Audience insights and targeting.' },
    { text: 'Engage', color: '#FF9F43', path: '/services/experience', description: 'Engagement‑focused experiences.' },
    { text: 'Convert', color: '#54A0FF', path: '/services/experience/cro', description: 'Conversion rate optimisation.' }
];

const benefits = [
    "We start with a clear plan — not random campaigns.",
    "From first click to final conversion — everything is connected.",
    "Every move we make is backed by performance data.",
    "We don’t chase short - term wins — we build long - term growth.",
];

const instagramColors = ['#833AB4', '#C13584', '#E1306C', '#FD1D1D', '#F56040', '#F77737', '#FCAF45', '#FFDC80'];

// --- Word component with link and tooltip ---
function Word({ children, color, path, description, ...props }) {
    const navigate = useNavigate();
    const currentColor = new THREE.Color(color)
    const hoverColor = new THREE.Color(instagramColors[Math.floor(Math.random() * instagramColors.length)])
    const fontProps = {
        font: '/Inter-Bold.woff',
        fontSize: 1.5,
        letterSpacing: -0.05,
        lineHeight: 1,
        'material-toneMapped': false
    }
    const ref = useRef()
    const [hovered, setHovered] = useState(false)
    const over = (e) => (e.stopPropagation(), setHovered(true))
    const out = () => setHovered(false)

    useEffect(() => {
        if (hovered) document.body.style.cursor = 'pointer'
        return () => (document.body.style.cursor = 'auto')
    }, [hovered])

    useFrame(({ camera }) => {
        if (ref.current && ref.current.material) {
            ref.current.material.color.lerp(
                hovered ? hoverColor : currentColor,
                0.1
            )
        }
    })

    const handleClick = () => {
        navigate(path);
    };

    return (
        <Billboard {...props}>
            <Text
                ref={ref}
                onPointerOver={over}
                onPointerOut={out}
                onClick={handleClick}
                color={color}
                {...fontProps}
                children={children}
            />
            {hovered && (
                <Html
                    position={[0, 2.2, 0]}
                    center
                    style={{
                        background: 'rgba(0,0,0,0.8)',
                        color: 'white',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        transform: 'translateX(-50%)',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                        border: '1px solid rgba(255,255,255,0.2)'
                    }}
                >
                    {description}
                </Html>
            )}
        </Billboard>
    )
}

// --- Cloud of words ---
function Cloud({ count = 6, radius = 15 }) {
    const words = useMemo(() => {
        const temp = []
        const spherical = new THREE.Spherical()
        const phiSpan = Math.PI / (count + 1)
        const thetaSpan = (Math.PI * 2) / count

        const shuffledWords = [...advertisingWords].sort(() => Math.random() - 0.5)

        let wordIndex = 0
        for (let i = 1; i < count + 1; i++) {
            for (let j = 0; j < count; j++) {
                const wordData = shuffledWords[wordIndex % shuffledWords.length]
                temp.push([
                    new THREE.Vector3().setFromSpherical(spherical.set(radius, phiSpan * i, thetaSpan * j)),
                    wordData.text,
                    wordData.color,
                    wordData.path,
                    wordData.description
                ])
                wordIndex++
            }
        }
        return temp
    }, [count, radius])

    return words.map(([pos, word, color, path, description], index) => (
        <Word key={index} position={pos} color={color} path={path} description={description} children={word} />
    ))
}

// --- Camera controller ---
function CameraController() {
    const timeRef = useRef(0)
    const [isVisible, setIsVisible] = useState(false)

    useFrame((state, delta) => {
        if (!isVisible) return

        timeRef.current += delta

        state.camera.position.x = Math.sin(timeRef.current * 0.2) * 30
        state.camera.position.y = Math.cos(timeRef.current * 0.3) * 3 + 3
        state.camera.position.z = Math.cos(timeRef.current * 0.2) * 30

        const zoomFactor = Math.sin(timeRef.current * 0.1) * 0.1
        const targetFOV = 60 + (zoomFactor * 20)
        state.camera.fov = THREE.MathUtils.lerp(state.camera.fov, targetFOV, 0.05)
        state.camera.lookAt(0, 0, 0)
        state.camera.updateProjectionMatrix()
    })

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => setIsVisible(entry.isIntersecting),
            { threshold: 0.2 }
        )
        const canvas = document.querySelector('.gb-background-canvas canvas')
        if (canvas) observer.observe(canvas)
        return () => observer.disconnect()
    }, [])

    return null
}

// --- Cloud controller ---
function CloudController() {
    const groupRef = useRef()
    const timeRef = useRef(0)
    const [isVisible, setIsVisible] = useState(false)

    useFrame((state, delta) => {
        if (!isVisible) return

        timeRef.current += delta

        groupRef.current.rotation.y = timeRef.current * 0.3
        groupRef.current.rotation.x = Math.sin(timeRef.current * 0.2) * 0.1
        groupRef.current.rotation.z = Math.cos(timeRef.current * 0.15) * 0.05

        const scale = 1 + Math.sin(timeRef.current * 0.3) * 0.02
        groupRef.current.scale.setScalar(scale)
    })

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => setIsVisible(entry.isIntersecting),
            { threshold: 0.2 }
        )
        const canvas = document.querySelector('.gb-background-canvas canvas')
        if (canvas) observer.observe(canvas)
        return () => observer.disconnect()
    }, [])

    return (
        <group ref={groupRef} rotation={[0, 0, 0]}>
            <Cloud count={6} radius={12} />
        </group>
    )
}

// --- Main component with purple-indigo gradient background ---
export default function App() {
    const [isVisible, setIsVisible] = useState(false)
    const sectionRef = useRef(null)

    useEffect(() => {
        const currentSection = sectionRef.current
        const observer = new IntersectionObserver(
            ([entry]) => setIsVisible(entry.isIntersecting),
            { threshold: 0.2 }
        )
        if (currentSection) observer.observe(currentSection)
        return () => { if (currentSection) observer.unobserve(currentSection) }
    }, [])

    return (
        <div
            ref={sectionRef}
            className={`whyreachtix gb-fullpage ${isVisible ? 'gb-visible' : ''}`}
        >
            {/* Main headline */}
            <div className='text-center'>
                <h1 className={`why-section-title ${isVisible ? 'gb-animated' : ''}`}>
                    Most Agencies Runs Ads,We build Growth Systems
                </h1>
                <div >
                    <h3 className="gb-left-subtitle" >We design structured marketing strategies that turn traffic into real, scalable revenue.
                    </h3>
                </div>
            </div>
            {/* Background Canvas - positioned to the right */}
            <div className="d-none d-lg-block gb-background-canvas">
                <Canvas
                    dpr={[1, 2]}
                    camera={{
                        position: [0, 0, 35],
                        fov: 70,
                        near: 0.3,
                        far: 4000
                    }}
                    style={{
                        width: '100%',
                        height: '100%',
                        opacity: isVisible ? 0.9 : 0,
                        transition: 'opacity 1.5s ease'
                    }}
                >
                    <fog attach="fog" args={['rgba(0, 0, 0, 0)', 0, 100]} />
                    <Suspense fallback={null}>
                        <CloudController />
                        <CameraController />
                        <ambientLight intensity={0.8} />
                        <pointLight position={[10, 10, 10]} intensity={3.0} color="#ffffff" />
                        <pointLight position={[-10, -10, -10]} intensity={1.0} color="#833AB4" />
                    </Suspense>
                </Canvas>
            </div>

            {/* Left-Aligned Content Overlay with padding top */}
            <div className="gb-content-overlay">
                <div className="gb-content-left">

                    {/* Benefits as a clean column */}
                    <div className={`gb-benefits-column ${isVisible ? 'gb-animated' : ''}`}>
                        {benefits.map((benefit, index) => (
                            <div key={index} className="gb-benefit-line" style={{ transitionDelay: `${0.2 + (index * 0.1)}s` }}>
                                <span className="gb-benefit-dot"></span>
                                <span className="gb-benefit-text">{benefit}</span>
                            </div>
                        ))}
                    </div>

                    {/* Simple separator line */}
                    <div className={`gb-separator-line ${isVisible ? 'gb-animated' : ''}`}></div>




                </div>
                {/* CTA Button */}
                <div className={`gb-cta-wrapper ${isVisible ? 'gb-animated' : ''}`}>
                    <Link to='contact' className="cta-button-info">
                        Book a Free Strategy Call
                        <span className="gb-cta-arrow">→</span>
                    </Link>
                </div>
            </div>
        </div>
    )
}